/**
 * Venues Duck
 *
 * Manages venue data from Supabase.
 * Handles fetching and searching venues.
 */

import { storableError } from '../util/errors';
import * as log from '../util/log';

// ================ Action types ================ //

export const SEARCH_VENUES_REQUEST = 'app/venues/SEARCH_VENUES_REQUEST';
export const SEARCH_VENUES_SUCCESS = 'app/venues/SEARCH_VENUES_SUCCESS';
export const SEARCH_VENUES_ERROR = 'app/venues/SEARCH_VENUES_ERROR';

export const FETCH_VENUE_DETAILS_REQUEST = 'app/venues/FETCH_VENUE_DETAILS_REQUEST';
export const FETCH_VENUE_DETAILS_SUCCESS = 'app/venues/FETCH_VENUE_DETAILS_SUCCESS';
export const FETCH_VENUE_DETAILS_ERROR = 'app/venues/FETCH_VENUE_DETAILS_ERROR';

export const CLEAR_VENUES = 'app/venues/CLEAR_VENUES';

// ================ Reducer ================ //

export const initialState = {
  searchResults: [],
  venueDetails: {},
  searchInProgress: false,
  searchError: null,
  fetchVenueDetailsInProgress: false,
  fetchVenueDetailsError: null,
};

export default function venuesReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case SEARCH_VENUES_REQUEST:
      return {
        ...state,
        searchInProgress: true,
        searchError: null,
      };
    case SEARCH_VENUES_SUCCESS:
      return {
        ...state,
        searchResults: payload.venues,
        searchInProgress: false,
      };
    case SEARCH_VENUES_ERROR:
      return {
        ...state,
        searchInProgress: false,
        searchError: payload,
      };

    case FETCH_VENUE_DETAILS_REQUEST:
      return {
        ...state,
        fetchVenueDetailsInProgress: true,
        fetchVenueDetailsError: null,
      };
    case FETCH_VENUE_DETAILS_SUCCESS:
      return {
        ...state,
        venueDetails: {
          ...state.venueDetails,
          [payload.venue.id]: payload.venue,
        },
        fetchVenueDetailsInProgress: false,
      };
    case FETCH_VENUE_DETAILS_ERROR:
      return {
        ...state,
        fetchVenueDetailsInProgress: false,
        fetchVenueDetailsError: payload,
      };

    case CLEAR_VENUES:
      return initialState;

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const getVenueSearchResults = state => state.venues?.searchResults || [];
export const getVenueDetails = (state, venueId) => state.venues?.venueDetails[venueId] || null;
export const isVenueActionInProgress = state =>
  state.venues?.searchInProgress || state.venues?.fetchVenueDetailsInProgress;

// ================ Action creators ================ //

const searchVenuesRequest = () => ({ type: SEARCH_VENUES_REQUEST });
const searchVenuesSuccess = venues => ({ type: SEARCH_VENUES_SUCCESS, payload: { venues } });
const searchVenuesError = error => ({ type: SEARCH_VENUES_ERROR, payload: error, error: true });

const fetchVenueDetailsRequest = () => ({ type: FETCH_VENUE_DETAILS_REQUEST });
const fetchVenueDetailsSuccess = venue => ({
  type: FETCH_VENUE_DETAILS_SUCCESS,
  payload: { venue },
});
const fetchVenueDetailsError = error => ({
  type: FETCH_VENUE_DETAILS_ERROR,
  payload: error,
  error: true,
});

export const clearVenues = () => ({ type: CLEAR_VENUES });

// ================ Thunks ================ //

/**
 * Search for venues
 */
export const searchVenues = (query = '', filters = {}) => dispatch => {
  dispatch(searchVenuesRequest());

  const params = new URLSearchParams({ q: query, ...filters });

  return fetch(`/api/supabase/venues/search?${params}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to search venues');
        });
      }
      return res.json();
    })
    .then(data => {
      dispatch(searchVenuesSuccess(data.venues || []));
      return data.venues;
    })
    .catch(e => {
      log.error(e, 'search-venues-failed');
      dispatch(searchVenuesError(storableError(e)));
      throw e;
    });
};

/**
 * Fetch details for a specific venue
 */
export const fetchVenueDetails = venueId => dispatch => {
  dispatch(fetchVenueDetailsRequest());

  return fetch(`/api/supabase/venues/${venueId}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to fetch venue details');
        });
      }
      return res.json();
    })
    .then(data => {
      dispatch(fetchVenueDetailsSuccess(data.venue));
      return data.venue;
    })
    .catch(e => {
      log.error(e, 'fetch-venue-details-failed');
      dispatch(fetchVenueDetailsError(storableError(e)));
      throw e;
    });
};
